import actions from '@src/redux/actions';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

export const useAudioPlayerMultiBlock = () => {
  const audioRef = useRef(new Audio());
  const audioInBlockRef = useRef(new Audio());
  const isSeeking = useRef(false);
  const prevCurrentBlockIdRef = useRef(null);
  const prevCurrentPlayingBlockIdRef = useRef(null);
  const prevAudioLinkRef = useRef(null);
  const isInitializedRef = useRef(false);

  const {
    currentBlockId,
    currentPlayingBlockId,
    isPlaying,
    isPlayingBlock,
    multiBlock,
    isMultiBlockMode,
  } = useSelector((state) => state.project);
  const isPlayingBlockRef = useRef(isPlayingBlock);
  const isPlayingRef = useRef(isPlaying);

  const { blocks, blockIds } = multiBlock || {};
  const dispatch = useDispatch();
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDurations, setAudioDurations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isErrorPlaying, setIsErrorPlaying] = useState(false);

  // Multi-audio integration - Memoized to prevent unnecessary re-creation
  const audioSources = useMemo(() => {
    if (!blocks || !blockIds) return [];

    const sources = blockIds
      .map((blockId) => ({
        audioLink: blocks[blockId]?.audioLink,
        blockId,
      }))
      .filter((block) => block.audioLink && block.audioLink.trim() !== '');

    return sources;
  }, [blocks, blockIds]);

  const audioLinks = useMemo(
    () => audioSources.map((b) => b.audioLink),
    [audioSources],
  );

  // Stable reference for audio operations
  const audioElementRef = useRef(audioRef.current);

  // Initialize audio element once
  useEffect(() => {
    if (!isInitializedRef.current) {
      audioElementRef.current = audioRef.current;
      isInitializedRef.current = true;
    }
  }, []);

  // Calculate total duration and current progress
  const totalDuration = useMemo(
    () => audioDurations.reduce((sum, duration) => sum + (duration || 0), 0),
    [audioDurations],
  );

  const currentTotalTime = useMemo(() => {
    const index = audioSources.findIndex((b) => b.blockId === currentBlockId);
    if (index === -1) {
      return 0;
    }
    return (
      audioDurations
        .slice(0, index)
        .reduce((sum, duration) => sum + (duration || 0), 0) + currentTime
    );
  }, [audioDurations, currentBlockId, currentTime, audioSources]);

  // Memoized function to prevent unnecessary re-creation
  const loadAudioMetadata = useCallback(
    async (src) =>
      new Promise((resolve) => {
        const tempAudio = new Audio();
        tempAudio.preload = 'metadata';

        const handleLoadedMetadata = () => {
          resolve(tempAudio.duration);
          tempAudio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        };

        tempAudio.addEventListener('loadedmetadata', handleLoadedMetadata);
        tempAudio.src = src;
      }),
    [],
  );

  // Only load metadata when audioSources actually changes (deep comparison)
  useEffect(() => {
    if (audioSources.length) {
      const loadAllMetadata = async () => {
        setIsLoading(true);
        setAudioDurations(new Array(audioSources.length).fill(0));

        const promises = audioSources.map((block) =>
          loadAudioMetadata(block.audioLink),
        );

        const durations = await Promise.all(promises);
        setAudioDurations(durations);
        setIsLoading(false);
      };

      if (audioSources.length > 0) {
        loadAllMetadata();
      } else {
        setAudioDurations([]);
        setIsLoading(false);
      }
    }
  }, [audioLinks.join(','), loadAudioMetadata]);

  const handleLoadAudio = (currentId) => {
    if (currentId === prevCurrentBlockIdRef.current) return;
    prevCurrentBlockIdRef.current = currentId;
    const audio = audioElementRef.current;
    const block = audioSources.find((b) => b.blockId === currentId);
    if (!block) return;
    audio.src = block.audioLink;
    if (isPlayingRef.current) {
      audio.play();
    }
  };

  // Memoized event handlers to prevent unnecessary re-creation
  const handleTimeUpdate = useCallback(() => {
    const time = audioElementRef.current.currentTime;
    setCurrentTime(time);
  }, []);

  const handleEnded = useCallback(() => {
    const index = audioSources.findIndex(
      (block) => block.blockId === currentBlockId,
    );
    isSeeking.current = false;

    if (!isMultiBlockMode) {
      // Single block mode: stop after current block ends
      dispatch(actions.project.setIsPlayingBlock(false));
      setCurrentTime(0);
    } else if (index < audioSources.length - 1) {
      // Multi block mode: continue to next block
      const nextBlockId = audioSources[index + 1].blockId;
      dispatch(actions.project.setCurrentBlockId(nextBlockId));
      setCurrentTime(0);
      handleLoadAudio(nextBlockId);
    } else {
      // All audios completed in multi block mode
      dispatch(actions.project.setIsPlaying(false));
      dispatch(actions.project.setCurrentBlockId(null));
      dispatch(actions.project.setIsMultiBlockMode(false));
    }
  }, [currentBlockId, audioSources, isMultiBlockMode, dispatch]);
  const handleEndedInBlock = () => {
    dispatch(actions.project.setIsPlayingBlock(false));
  };

  useEffect(() => {
    const audio = audioInBlockRef.current;

    audio.addEventListener('ended', handleEndedInBlock);

    return () => {
      audio.removeEventListener('ended', handleEndedInBlock);
    };
  }, [handleEnded]);
  useEffect(() => {
    const audio = audioElementRef.current;

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [handleTimeUpdate, handleEnded]);

  useEffect(() => {
    isPlayingBlockRef.current = isPlayingBlock;
  }, [isPlayingBlock]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  useEffect(() => {
    prevCurrentPlayingBlockIdRef.current = currentPlayingBlockId;
  }, [currentPlayingBlockId]);

  useEffect(() => {
    const audio = audioElementRef.current;
    if (isPlaying) {
      audio.play().catch((error) => {
        const ERROR_DUE_TO_EXPIRED_AUDIO_LINK = 'NotSupportedError';
        if (error.name === ERROR_DUE_TO_EXPIRED_AUDIO_LINK) {
          setIsErrorPlaying(true);
        }
      });
    } else {
      audio.pause();
    }
  }, [isPlaying]);
  useEffect(() => {
    const audio = audioInBlockRef.current;
    if (isPlayingBlock) {
      audio.play().catch((error) => {
        const ERROR_DUE_TO_EXPIRED_AUDIO_LINK = 'NotSupportedError';
        if (error.name === ERROR_DUE_TO_EXPIRED_AUDIO_LINK) {
          setIsErrorPlaying(true);
        }
      });
    } else {
      audio.pause();
    }
  }, [isPlayingBlock]);

  useEffect(
    () => () => {
      const audio = audioElementRef.current;
      const blockAudio = audioInBlockRef.current;
      blockAudio.pause();
      blockAudio.currentTime = 0;
      audio.pause();
      audio.currentTime = 0;
      dispatch(actions.project.setCurrentBlockId(null));
    },
    [dispatch],
  );

  const handleChangeVolume = useCallback((value) => {
    audioElementRef.current.volume = value / 100;
  }, []);

  const handlePlayAudioInBlock = (blockId, audioExpiredAt) => {
    const audioHasExpired = audioExpiredAt && moment().isAfter(audioExpiredAt);
    if (audioHasExpired) {
      dispatch(
        actions.project.updateBlockSynthesisInfo(blockId, {
          audioLink: null,
        }),
      );
      return;
    }

    isSeeking.current = false;
    // This is single block play (playing from individual block button)
    dispatch(actions.project.setIsMultiBlockMode(false));

    if (
      prevCurrentPlayingBlockIdRef.current !== blockId ||
      prevAudioLinkRef.current !== blocks[blockId]?.audioLink
    ) {
      const audioLink = blocks[blockId]?.audioLink;
      prevAudioLinkRef.current = audioLink;
      dispatch(actions.project.setAudioLink(audioLink));
      dispatch(actions.project.setCurrentPlayingBlockId(blockId));
      audioInBlockRef.current.src = audioLink;
      audioInBlockRef.current.play();
    }
    dispatch(actions.project.setIsPlayingBlock(true));
    dispatch(actions.project.setIsPlaying(false));
  };

  const handlePlay = useCallback(() => {
    // Find all expired audio and delete the audio link
    // which result in user will have to regenerate the block
    const audioExpiredBlocks = Object.values(blocks || {}).filter((block) =>
      moment().isAfter(block.audioExpiredAt),
    );
    if (audioExpiredBlocks.length > 0) {
      const audioExpiredBlockIds = audioExpiredBlocks.map((block) => block.id);
      const synthesisInfos = audioExpiredBlocks.map((block) => ({
        ...block,
        audioLink: null,
      }));
      dispatch(
        actions.project.updateMultiBlockSynthesisInfo(
          audioExpiredBlockIds,
          synthesisInfos,
        ),
      );
      return;
    }
    if (audioSources.length === 0) return;

    // This is multi-block play (playing from the main audio player)
    dispatch(actions.project.setIsMultiBlockMode(true));
    dispatch(actions.project.setIsPlayingBlock(false));
    dispatch(actions.project.setIsPlaying(true));
    let blockId = currentBlockId;
    if (!blockId) {
      blockId = audioSources[0].blockId;
      dispatch(actions.project.setCurrentBlockId(blockId));
    }
    handleLoadAudio(blockId);
  }, [blocks, audioSources, currentBlockId, dispatch]);

  const handlePause = useCallback(() => {
    dispatch(actions.project.setIsPlaying(false));
  }, [dispatch]);

  const handleSeeked = (value) => {
    if (totalDuration === 0) return;

    const targetTime = value;

    // Find which audio this time corresponds to
    let accumulatedTime = 0;
    let targetAudioIndex = 0;
    let targetAudioTime = 0;

    for (let i = 0; i < audioDurations.length; i += 1) {
      const duration = audioDurations[i] || 0;
      if (accumulatedTime + duration >= targetTime) {
        targetAudioIndex = i;
        targetAudioTime = targetTime - accumulatedTime;
        break;
      }
      accumulatedTime += duration;
    }

    isSeeking.current = true;
    const audio = audioElementRef.current;
    const targetBlockId = audioSources[targetAudioIndex]?.blockId;
    dispatch(actions.project.setCurrentBlockId(targetBlockId));
    dispatch(actions.project.setIsMultiBlockMode(true));

    audio.src = audioSources[targetAudioIndex]?.audioLink;
    if (isPlayingRef.current) {
      audio.play();
    }
    audio.currentTime = targetAudioTime;
    setCurrentTime(targetAudioTime);
  };

  return {
    audioRef,
    handlePlay,
    handlePause,
    handleSeeked,
    isLoading,
    isErrorPlaying,
    isPlaying,
    currentTime: currentTotalTime,
    duration: totalDuration,
    handleChangeVolume,
    handlePlayAudioInBlock,
    handleLoadAudio,
  };
};
